import React, { useCallback, useEffect, useRef, useState } from 'react'

import { c } from '../../translations/LibraryTranslationService'
import styles from './_filter.module.scss'
import { getTextWidth } from './exposedFilter-helper'
import {
  Button,
  CheckboxState,
  MultiSelect,
  Popover,
  type PopoverProps,
  TrimText,
  useIsClient,
  useIsMobileView,
} from '../../module'
import { type MultiSelectDataItemBase } from '../MultiSelect/MultiSelect'
import {
  type ExposedFilterNameType,
  type FilterValue,
  type MultiSelectFilterProps,
} from './ExposedFilter.models'

const ExposedMultiSelectFilter = <
  FilterType extends MultiSelectDataItemBase & ExposedFilterNameType,
>(
  filterValues: MultiSelectFilterProps<FilterType> & {
    stateName: string
    labelName: string
    position?: PopoverProps['position']
    disableCloseOnScroll?: PopoverProps['disableCloseOnScroll']
    onChangeCallout?: (
      stateName: string,
      value?: FilterValue<FilterType>,
    ) => void
    isClearFilter?: boolean
    setIsClearFilter?: (value: boolean) => void
  },
): React.JSX.Element => {
  const {
    labelName,
    position,
    disableCloseOnScroll,
    onChangeCallout,
    stateName,
    isClearFilter,
    setIsClearFilter,
    multiSelectProps,
  } = filterValues
  const containerRef = useRef<HTMLDivElement>(null)
  const isMobile = useIsMobileView()
  const [selectedTags, setSelectedTags] = useState<FilterType[]>(
    multiSelectProps.selectedOptions ? multiSelectProps.selectedOptions : [],
  )
  const [hasMore, setHasMore] = useState(false)
  const MAX_WIDTH_FILTER_CONTAINER = (isMobile ? 240 : 300) - 16 - 16
  const MORE_INDICATOR_RESERVED_WIDTH = 40
  const CLEAR_BUTTON_WIDTH = 38
  const RIGHT_PADDING = 8
  const isClient = useIsClient()
  const [truncateTagIndex, setTruncateTagIndex] = useState<number>(-1)
  const [truncatedTagWidth, setTruncatedTagWidth] = useState<number>(0)

  const showSelectedFilters = useCallback(
    (selectedOptions: FilterType[]) => {
      if (!isClient) return

      const labelWidth = getTextWidth(labelName)
      const currentAvailableWidth =
        MAX_WIDTH_FILTER_CONTAINER -
        labelWidth -
        CLEAR_BUTTON_WIDTH -
        RIGHT_PADDING

      let totalWidth = 0
      const updatedTags: FilterType[] = []
      let truncateLastTagIndex = -1
      let remainingWidthForTruncatedTag = 0

      // First pass: add as many complete tags as possible
      for (let i = 0; i < selectedOptions.length; i++) {
        const option = selectedOptions[i]
        const tagWidth = getTextWidth(option.name)
        const remainingTags = selectedOptions.length - i - 1

        // Check if we need to reserve space for "+X" indicator
        const reservedWidth =
          remainingTags > 0 ? MORE_INDICATOR_RESERVED_WIDTH : 0

        // Calculate gap for this tag (4px gap between tags)
        const gapWidth = updatedTags.length > 1 ? 4 : 0

        // If we can fit this tag completely (including its gap)
        if (
          totalWidth + tagWidth + gapWidth <=
          currentAvailableWidth - reservedWidth
        ) {
          totalWidth += tagWidth + gapWidth
          updatedTags.push(option)
          // If we have some space left, calculate how much space we can give to the truncated tag
        } else {
          const remainingSpace =
            currentAvailableWidth -
            totalWidth -
            gapWidth -
            MORE_INDICATOR_RESERVED_WIDTH
          if (remainingSpace >= 20) {
            // Minimum reasonable width for truncation
            updatedTags.push(option)
            truncateLastTagIndex = updatedTags.length - 1
            remainingWidthForTruncatedTag = remainingSpace
            break
          }
          break
        }
      }
      setSelectedTags(updatedTags)
      setHasMore(updatedTags.length < selectedOptions.length)
      setTruncateTagIndex(truncateLastTagIndex)
      setTruncatedTagWidth(remainingWidthForTruncatedTag)
    },
    [isClient, MAX_WIDTH_FILTER_CONTAINER, labelName],
  )

  useEffect(() => {
    if (multiSelectProps.selectedOptions) {
      showSelectedFilters(multiSelectProps.selectedOptions)
    }
  }, [multiSelectProps.selectedOptions, showSelectedFilters])

  const resetFilters = useCallback(() => {
    if (multiSelectProps.nestedConfig) {
      const resetItemStates = multiSelectProps.nestedConfig.itemStates.map(
        (item) => ({
          ...item,
          state: CheckboxState.UNCHECKED,
        }),
      )
      multiSelectProps.nestedConfig.setItemStates(resetItemStates)
    }
    onChangeCallout?.(stateName, [])
  }, [multiSelectProps.nestedConfig, onChangeCallout, stateName])

  // Clear filter when clear all is clicked
  useEffect(() => {
    if (isClearFilter) {
      resetFilters()
      setIsClearFilter?.(false)
    }
  }, [isClearFilter, setIsClearFilter, resetFilters])

  const multiSelectSharedProps = {
    selectedOptions: multiSelectProps.selectedOptions,
    options: multiSelectProps.options,
    labelKey: multiSelectProps.labelKey,
    callout: (selectedOptions: FilterType[]) =>
      onChangeCallout?.(stateName, selectedOptions),
    formLabelProps: multiSelectProps.formLabelProps,
    loading: multiSelectProps.loading ?? false,
    searchBarProps: {
      ...multiSelectProps.searchBarProps,
      autoFocus: true,
    },
    isReorderable: multiSelectProps.isReorderable,
    disabled: multiSelectProps.disabled,
    hideSelectAll: multiSelectProps.hideSelectAll,
    sortAlphabetically: multiSelectProps.sortAlphabetically,
    exposed: true,
    removeBorder: true,
  }

  return (
    <Popover
      position={position ?? 'bottom-start'}
      disableCloseOnScroll={disableCloseOnScroll ?? true}
      noPadding
      popoverContent={
        multiSelectProps.nestedConfig ? (
          <MultiSelect
            {...multiSelectSharedProps}
            nestedConfig={multiSelectProps.nestedConfig}
          />
        ) : (
          <MultiSelect {...multiSelectSharedProps} />
        )
      }
    >
      {({ visible, setVisible }) => (
        <div
          ref={containerRef}
          className={`${styles.filterContainer} ${selectedTags.length > 0 ? styles.active : ''}`}
        >
          <Button as='unstyled' onClick={() => setVisible(!visible)}>
            <div
              className={`${styles.flexContainer} ${
                multiSelectProps.selectedOptions?.length > 0
                  ? styles.withClearButton
                  : ''
              }`}
            >
              <TrimText
                customClass={`${selectedTags.length > 0 ? styles.fontStyled : styles.fontNotSelected} ${styles.labelContainer}`}
                text={labelName}
              />
              {multiSelectProps.selectedOptions?.length > 0 ? (
                <div className={styles.optionContainer}>
                  {selectedTags.map((option, index) =>
                    index === truncateTagIndex ? (
                      <div
                        key={index}
                        className={styles.optionName}
                        style={{ maxWidth: `${truncatedTagWidth}px` }}
                      >
                        <TrimText text={option.name} />
                      </div>
                    ) : (
                      <span key={index} className={styles.optionName}>
                        {option.name}
                      </span>
                    ),
                  )}
                  {hasMore ? (
                    <span className={styles.moreIndicator}>
                      +
                      {multiSelectProps.selectedOptions?.length -
                        selectedTags.length}
                    </span>
                  ) : null}
                </div>
              ) : (
                <span className={styles.textPurple}>{c('none')}</span>
              )}
            </div>
          </Button>
          {multiSelectProps.selectedOptions?.length > 0 ? (
            <Button
              as='unstyled'
              className={styles.clearButton}
              onClick={resetFilters}
            >
              {c('clear')}
            </Button>
          ) : null}
        </div>
      )}
    </Popover>
  )
}

export default ExposedMultiSelectFilter
